/** biome-ignore-all lint/suspicious/noExplicitAny: external dependency code */
/** biome-ignore-all lint/performance/noAccumulatingSpread: external dependency code */
/**
 * https://github.com/joe-bell/cva/blob/main/packages/class-variance-authority/src/index.ts
 */
import { type ClassValue, cn } from "../cn";

export type ClassPropKey = "class" | "className";

export type ClassProp =
	| {
			class: ClassValue;
			className?: never;
	  }
	| { class?: never; className: ClassValue }
	| { class?: never; className?: never };

export type OmitUndefined<T> = T extends undefined ? never : T;
export type StringToBoolean<T> = T extends "true" | "false" ? boolean : T;

export type VariantProps<Component extends (...args: any) => any> = Omit<
	OmitUndefined<Parameters<Component>[0]>,
	"class" | "className"
>;

const falsyToString = <T>(value: T) =>
	typeof value === "boolean" ? `${value}` : value === 0 ? "0" : value;

type ConfigSchema = Record<string, Record<string, ClassValue>>;

type ConfigVariants<T extends ConfigSchema> = {
	[Variant in keyof T]?: StringToBoolean<keyof T[Variant]> | null | undefined;
};
type ConfigVariantsMulti<T extends ConfigSchema> = {
	[Variant in keyof T]?:
		| StringToBoolean<keyof T[Variant]>
		| StringToBoolean<keyof T[Variant]>[]
		| undefined;
};

type Config<T> = T extends ConfigSchema
	? {
			variants?: T;
			defaultVariants?: ConfigVariants<T>;
			compoundVariants?: (T extends ConfigSchema
				? (ConfigVariants<T> | ConfigVariantsMulti<T>) & ClassProp
				: ClassProp)[];
		}
	: never;

type Props<T> = T extends ConfigSchema
	? ConfigVariants<T> & ClassProp
	: ClassProp;

export const cva =
	<T extends Record<string, ClassValue>>(
		base?: ClassValue,
		config?: Config<T>,
	) =>
	(props?: Props<T>) => {
		if (config?.variants == null)
			return cn(base, props?.class, props?.className);

		const { variants, defaultVariants } = config;

		const getVariantClassNames = Object.keys(variants).map(
			(variant: keyof typeof variants) => {
				const variantProp = props?.[variant as keyof typeof props];
				const defaultVariantProp = defaultVariants?.[variant];

				if (variantProp === null) return null;

				const variantKey = (falsyToString(variantProp) ||
					falsyToString(
						defaultVariantProp,
					)) as keyof (typeof variants)[typeof variant];

				return variants?.[variant]?.[variantKey] as Record<string, ClassValue>;
			},
		);

		const propsWithoutUndefined =
			props &&
			Object.entries(props).reduce(
				(acc, [key, value]) => {
					if (value === undefined) {
						return acc;
					}

					acc[key] = value;
					return acc;
				},
				{} as Record<string, unknown>,
			);

		const getCompoundVariantClassNames = config?.compoundVariants?.reduce(
			(
				acc,
				{ class: cvClass, className: cvClassName, ...compoundVariantOptions },
			) =>
				Object.entries(compoundVariantOptions).every(([key, value]) =>
					Array.isArray(value)
						? value.includes(
								{
									...defaultVariants,
									...propsWithoutUndefined,
								}[key],
							)
						: {
								...defaultVariants,
								...propsWithoutUndefined,
							}[key] === value,
				)
					? [...acc, cvClass, cvClassName]
					: acc,
			[] as ClassValue[],
		);

		return cn(
			base,
			getVariantClassNames,
			getCompoundVariantClassNames,
			props?.class,
			props?.className,
		);
	};
